import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from './Logo';
import ContactPopup from './ContactPopup';
import WhatsAppFloat from './WhatsAppFloat';
import SolidPartnersMarquee from './solid/SolidPartnersMarquee';

const Layout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const onHero = isHome && !isScrolled;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  const navItemClass = (path) => {
    const active = isActive(path);
    if (onHero) {
      return `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
        active ? 'text-white' : 'text-white/80 hover:text-white'
      }`;
    }
    return `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      active ? 'text-[#234a88]' : 'text-slate-600 hover:text-[#234a88]'
    }`;
  };

  const loginBtnClass = onHero
    ? 'rounded-md bg-white px-5 py-2.5 text-sm font-semibold text-[#234a88] transition-colors hover:bg-white/90'
    : 'rounded-md bg-[#234a88] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1c3b6d]';

  return (
    <div className="min-h-screen overflow-x-hidden bg-white font-display w-full max-w-full">
      <header
        className={`fixed left-0 right-0 top-0 z-50 w-full transition-all duration-300 ${
          onHero ? 'border-transparent bg-transparent' : 'border-b border-slate-200/80 bg-white/95 shadow-sm backdrop-blur-md'
        }`}
      >
        <nav className="container mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Logo variant={onHero ? 'onDark' : 'default'} />

          <div className="hidden items-center gap-0.5 lg:flex">
            <Link to="/" className={navItemClass('/')}>
              Home
            </Link>
            <Link to="/services" className={navItemClass('/services')}>
              Services
            </Link>
            <Link to="/projects" className={navItemClass('/projects')}>
              Projects
            </Link>
            <Link to="/cost-calculator" className={navItemClass('/cost-calculator')}>
              Cost Calculator
            </Link>
            <Link to="/about" className={navItemClass('/about')}>
              About
            </Link>
            <Link to="/contact" className={navItemClass('/contact')}>
              Contact
            </Link>
            <Link to="/login" className={`${loginBtnClass} ml-3`}>
              Client Login
            </Link>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <Link to="/login" className={`${loginBtnClass} whitespace-nowrap px-3 py-2 text-xs sm:px-4 sm:text-sm`}>
              Login
            </Link>
            <button
              type="button"
              className={`rounded-md p-2 focus:outline-none ${onHero ? 'text-white' : 'text-slate-800'}`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-expanded={isMenuOpen}
              aria-label="Menu"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </nav>

        {isMenuOpen && (
          <div className="border-t border-slate-200 bg-white px-4 py-4 shadow-lg lg:hidden">
            <div className="container mx-auto flex max-w-7xl flex-col gap-1">
              {[
                ['/', 'Home'],
                ['/services', 'Services'],
                ['/projects', 'Projects'],
                ['/cost-calculator', 'Cost Calculator'],
                ['/about', 'About'],
                ['/contact', 'Contact'],
              ].map(([path, label]) => (
                <Link
                  key={path}
                  to={path}
                  className={`rounded-md px-4 py-3 text-sm font-medium ${
                    isActive(path) ? 'bg-slate-100 text-[#234a88]' : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>

      <main>{children}</main>

      <SolidPartnersMarquee />
      <ContactPopup />
      <WhatsAppFloat />

      <footer className="relative mt-0 w-full overflow-hidden bg-[#234a88] text-white">
        <div className="container mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid gap-12 md:grid-cols-4">
            <div>
              <div className="mb-4 [&_img]:brightness-0 [&_img]:invert">
                <Logo variant="default" />
              </div>
              <p className="text-sm leading-relaxed text-white/60">
                Building excellence since 2005. Your trusted construction partner in Bangalore.
              </p>
            </div>

            <div>
              <h4 className="mb-6 text-xs font-semibold uppercase tracking-widest text-white/80">Quick Links</h4>
              <ul className="space-y-3 text-sm">
                {[
                  ['/', 'Home'],
                  ['/services', 'Services'],
                  ['/projects', 'Projects'],
                  ['/cost-calculator', 'Cost Calculator'],
                  ['/about', 'About'],
                  ['/contact', 'Contact'],
                ].map(([path, label]) => (
                  <li key={path}>
                    <Link to={path} className="text-white/55 transition-colors hover:text-white">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="mb-6 text-xs font-semibold uppercase tracking-widest text-white/80">Services</h4>
              <ul className="space-y-3 text-sm">
                {['Residential Construction', 'Commercial Construction', 'Apartments & Villas', 'Rental Units', 'Renovation'].map(
                  (s) => (
                    <li key={s}>
                      <Link to="/services" className="text-white/55 transition-colors hover:text-white">
                        {s}
                      </Link>
                    </li>
                  )
                )}
              </ul>
            </div>

            <div>
              <h4 className="mb-6 text-xs font-semibold uppercase tracking-widest text-white/80">Contact</h4>
              <ul className="space-y-4 text-sm text-white/55">
                <li className="flex gap-3">
                  <svg className="mt-0.5 h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <a href="mailto:bhuvilifespaces@gmail.com" className="transition-colors hover:text-white">
                    bhuvilifespaces@gmail.com
                  </a>
                </li>
                <li className="flex gap-3">
                  <svg className="mt-0.5 h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <a href="tel:+919741155118" className="transition-colors hover:text-white">
                    +91 97411 55118
                  </a>
                </li>
                <li className="flex gap-3">
                  <svg className="mt-0.5 h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>
                    Bhuvi Life Spaces, 8th main, Aecs layout,
                    <br />
                    A block, Singasandra, Bangalore - 560068
                  </span>
                </li>
                <li className="flex gap-3 border-t border-white/10 pt-4 text-white/55">
                  <svg className="mt-0.5 h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>
                    <span className="text-white/80">Business Hours:</span>
                    <br />
                    Monday - Saturday: 9:30 AM - 5:30 PM
                    <br />
                    Sunday: Appointment Based
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10">
          <div className="container mx-auto flex max-w-7xl flex-col items-center justify-center gap-4 px-4 py-6 sm:px-6 md:flex-row md:justify-between lg:px-8">
            <p className="text-center text-xs text-white/40 md:text-left">
              © {new Date().getFullYear()} Bhuvi Life Spaces. All rights reserved.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
              <Link to="/faqs" className="text-xs text-white/40 transition-colors hover:text-white">
                FAQs
              </Link>
              <Link to="/terms-and-conditions" className="text-xs text-white/40 transition-colors hover:text-white">
                Terms and Conditions
              </Link>
              <Link to="/privacy-policy" className="text-xs text-white/40 transition-colors hover:text-white">
                Privacy Policy
              </Link>
              <Link to="/return-refund-policy" className="text-xs text-white/40 transition-colors hover:text-white">
                Return & Refund Policy
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
